import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { RRule } from 'rrule';
import { normalizeTaskDueDate } from '@/modules/common/helpers/normalizeTaskDueDate.ts';
import { getNowInTaskTimeZone } from '@/modules/common/helpers/taskDateTime.ts';
import {
  TaskListWithRelationsResponse,
  TaskListWithRelationsSummaryResponse,
} from 'compliance-api';

dayjs.extend(utc);
dayjs.extend(timezone);

export const normalizeRRule = (rule: string): string => {
  if (!rule) return rule;

  return rule.replace(/UNTIL=(\d{8}T\d{6})([+-]\d{4})/, (_, datetime, offset) => {
    try {
      const localDate = dayjs(datetime + offset, 'YYYYMMDDTHHmmssZZ');

      const utcDate = localDate.utc().format('YYYYMMDDTHHmmss[Z]');

      return `UNTIL=${utcDate}`;
    } catch {
      return `UNTIL=${datetime}Z`;
    }
  });
};

export const checkIfTaskOverdueSoon = (
  task: TaskListWithRelationsResponse | TaskListWithRelationsSummaryResponse,
) => {
  const { overdue_date, recurrency_rule, created_at } = task;
  const now = getNowInTaskTimeZone();
  const isTaskOverdue = now.isAfter(normalizeTaskDueDate(task.overdue_date));

  if (isTaskOverdue) return false;

  let startIntervalDate: Date;
  if (recurrency_rule) {
    const rulePart = recurrency_rule.includes('RRULE:')
      ? recurrency_rule.split('RRULE:')[1]
      : recurrency_rule;
    const normalizeR = normalizeRRule(rulePart);
    const fromString = RRule.fromString(normalizeR);
    startIntervalDate = getRRulePrevDate(fromString);
  } else {
    startIntervalDate = dayjs(created_at).toDate();
  }

  const diffBetweenStartAndOverdue = dayjs(normalizeTaskDueDate(overdue_date)).diff(
    dayjs(startIntervalDate),
  );

  const diffBetweenNowAndOverdue = dayjs(normalizeTaskDueDate(overdue_date)).diff(now);

  return diffBetweenStartAndOverdue / 10 > diffBetweenNowAndOverdue;
};

const getRRulePrevDate = (rrule: RRule): Date => {
  const before = rrule.before(new Date(), true);

  if (before) return before;

  const after = rrule.after(new Date(), true);

  if (!after) return new Date();

  const afterAfter = rrule.after(after, true);

  if (!afterAfter) return new Date();

  const diffBetweenAfterAndNextExecution = dayjs(afterAfter).diff(after);

  return dayjs().subtract(diffBetweenAfterAndNextExecution, 'milliseconds').toDate();
};
